import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { APIProvider, Map, AdvancedMarker, Pin } from '@vis.gl/react-google-maps';
import RoutesManager from '../components/RoutesManager';
import RestaurantMarker from '../components/RestaurantMarker';
import { 
  FiMessageCircle, 
  FiMap, 
  FiPieChart, 
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiBarChart2,
  FiEye
} from 'react-icons/fi';

const Dashboard = () => {
  const [selectedService, setSelectedService] = useState('whatsapp');
  const navigate = useNavigate();

  // Configurações do Google Maps
  const mapContainerStyle = {
    width: '100%',
    height: '400px'
  };

  // Centro calculado baseado na média dos pontos de rota para melhor visualização
  const center = {
    lat: -25.463, // Ajustado para centralizar melhor nas rotas
    lng: -49.265  // Ajustado para centralizar melhor nas rotas
  };

  // Coordenadas dos pontos de rota
  const pontoSaida = { lat: -25.452331, lng: -49.254341, name: 'Restaurante Prado Velho' };
  const ponto1 = { lat: -25.476073, lng: -49.262600, name: 'Ponto 1' };
  const ponto2 = { lat: -25.476694, lng: -49.275646, name: 'Ponto 2' };
  const ponto3 = { lat: -25.460128, lng: -49.276354, name: 'Ponto 3' };

  const services = [
    { id: 'whatsapp', name: 'WhatsApp IA', icon: <FiMessageCircle /> },
    { id: 'routes', name: 'Rotas', icon: <FiMap /> },
    { id: 'waste', name: 'Desperdício', icon: <FiPieChart /> },
    { id: 'finance', name: 'Financeiro', icon: <FiDollarSign /> }
  ];

  const whatsappMetrics = {
    overview: [
      { title: 'Total de Interações', value: '382', change: 'Total acumulado', trend: 'neutral', color: 'blue' },
      { title: 'Tempo de Resposta', value: '9.94s', change: 'Média em segundos', trend: 'neutral', color: 'green' },
      { title: 'Intervenções Humanas', value: '53', change: 'Total registrado', trend: 'neutral', color: 'purple' },
      { title: 'Taxa de Automação', value: '86%', change: '', trend: 'neutral', color: 'orange' }
    ],
    recentMessages: [
      { contact: 'João Silva', message: 'Quero saber sobre o delivery', time: '2min', status: 'respondido' },
      { contact: 'Maria Santos', message: 'Gostaria de atendimento humano', time: '5min', status: 'pendente' },
      { contact: 'Carlos Lima', message: 'Cardápio completo por favor', time: '8min', status: 'respondido' },
      { contact: 'Ana Costa', message: 'Promoções do dia?', time: '12min', status: 'respondido' }
    ]
  };

  const routesMetrics = {
    overview: [
      { title: 'Rotas Ativas', value: '3', change: '', trend: 'neutral', color: 'blue' },
      { title: 'Entregas Programadas', value: '8', change: '', trend: 'neutral', color: 'green' },
      { title: 'Tempo de Entrega', value: '22min', change: '', trend: 'neutral', color: 'purple' },
      { title: 'Elogios na Entrega ', value: '80%', change: '', trend: 'neutral', color: 'orange' }
    ],
    restaurants: [
      {
        id: 1,
        name: 'Restaurante Prado Velho',
        color: 'blue',
        routes: [
          { id: 'A', name: 'Ponto A', destination: 'Saída → Ponto 1', deliveries: 6, status: 'Entregue', eta: '15min' },
          { id: 'B', name: 'Ponto B', destination: 'Ponto 1 → Ponto 2', deliveries: 2, status: 'Entregue', eta: '18min' },
          { id: 'C', name: 'Ponto C', destination: 'Ponto 2 → Ponto 3', deliveries: 3, status: 'A caminho', eta: '22min' }
        ]
      }
    ]
  };

  const wasteMetrics = {
    overview: [
      { title: '% de Desperdício Nessa Semana', value: '29%', change: '', trend: 'neutral', color: 'blue' },
      { title: 'Desperdicio Total', value: '25%', change: '', trend: 'neutral', color: 'green' },
      { title: 'Peso Total', value: '36kg', change: '', trend: 'neutral', color: 'purple' },
      { title: 'Peixes Processados', value: '24', change: '', trend: 'neutral', color: 'orange' }
    ],
    recentCuts: [
      { sushiman: 'Sushiman Gustavo Santos', fish: 'Salmão', waste: '30%', value: ''},
      { sushiman: 'Sushiman Amanda Silva', fish: 'Robalo', waste: '20%', value: ''},
      { sushiman: 'Sushiman Matheus Oliveira', fish: 'Tilápia', waste: '25%', value: ''},
      { sushiman: 'Sushiman Fernanda Costa', fish: 'Linguado', waste: '28%', value: ''}
    ]
  };

  const financeMetrics = {
    overview: [
      { title: 'Entradas Hoje', value: 'R$ 2.450,00', change: '', trend: 'neutral', color: 'blue' },
      { title: 'Despesas Hoje', value: 'R$ 1.552,00', change: '', trend: 'neutral', color: 'green' },
      { title: 'Extrato Diario', value: '+ R$ 898,00', change: '', trend: 'neutral', color: 'purple' },
      { title: 'Transações', value: '54', change: '', trend: 'neutral', color: 'orange' }
    ],
    recentTransactions: [
      { description: 'Vendas Delivery', value: 'R$ 818,00', type: 'receita', time: '10min' },
      { description: 'Compra de Ingredientes', value: 'R$ 424,00', type: 'despesa', time: '1h' },
      { description: 'Vendas Balcão', value: 'R$ 1632,00', type: 'receita', time: '2h' },
      { description: 'Pagamento de Fornecedor', value: 'R$ 848,00', type: 'despesa', time: '3h' },
      { description: 'Pagamento de Taxas e Motoboy', value: 'R$ 280,00', type: 'despesa', time: '3h' }
    ]
  };

  const getCurrentMetrics = () => {
    switch (selectedService) {
      case 'whatsapp': return whatsappMetrics;
      case 'routes': return routesMetrics;
      case 'waste': return wasteMetrics;
      case 'finance': return financeMetrics;
      default: return whatsappMetrics;
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600',
      orange: 'bg-orange-100 text-orange-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="pt-20 pb-12 bg-gradient-to-br from-slate-900 via-gray-900 to-zinc-900 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Dashboard <span className="bg-gradient-to-r from-blue-400 to-green-400 bg-clip-text text-transparent">AyTech</span>
            </h1>
            <p className="text-lg text-gray-300">
              Demonstração estática de nossas soluções em ação
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Selector */}
      <section className="py-8 bg-gradient-to-r from-slate-800 to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4">
            {services.map((service) => (
              <motion.button
                key={service.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedService(service.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  selectedService === service.id
                    ? 'bg-primary-600 text-white shadow-lg'
                    : 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600'
                }`}
              >
                {service.icon}
                <span>{service.name}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics Overview */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {getCurrentMetrics().overview.map((metric, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg card-hover"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${getColorClasses(metric.color)}`}>
                    <FiBarChart2 className="w-6 h-6" />
                  </div>
                  {metric.trend !== 'neutral' && (
                    <div className={`flex items-center text-sm font-semibold ${
                      metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {metric.trend === 'up' ? <FiTrendingUp className="w-4 h-4 mr-1" /> : <FiTrendingDown className="w-4 h-4 mr-1" />}
                      {metric.change}
                    </div>
                  )}
                  {metric.trend === 'neutral' && (
                    <div className="text-sm text-gray-500 font-medium">
                      {metric.change}
                    </div>
                  )}
                </div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">{metric.title}</h3>
                <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Views */}
      <section>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Recent Activity - Ocultar quando routes estiver selecionado */}
          {selectedService !== 'routes' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-6">
                {selectedService === 'whatsapp' && 'Exemplo de Mensagens Recentes'}
                {selectedService === 'waste' && 'Cortes Recentes'}
                {selectedService === 'finance' && 'Transações Recentes'}
              </h3>
              
              <div className="space-y-4">
                {selectedService === 'whatsapp' && whatsappMetrics.recentMessages.map((msg, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {msg.contact.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{msg.contact}</p>
                        <p className="text-sm text-gray-600 truncate max-w-xs">{msg.message}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">{msg.time}</p>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        msg.status === 'respondido' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {msg.status}
                      </span>
                    </div>
                  </div>
                ))}

                {selectedService === 'waste' && wasteMetrics.recentCuts.map((cut, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
                        <FiPieChart className="text-white w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{cut.sushiman}</p>
                        <p className="text-sm text-gray-600">{cut.fish} • Desperdício: {cut.waste}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-900 font-semibold">{cut.value}</p>
                    </div>
                  </div>
                ))}

                {selectedService === 'finance' && financeMetrics.recentTransactions.map((transaction, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        transaction.type === 'receita' ? 'bg-green-600' : 'bg-red-600'
                      }`}>
                        <FiDollarSign className="text-white w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{transaction.description}</p>
                        <p className="text-sm text-gray-600">{transaction.type}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${
                        transaction.type === 'receita' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.type === 'receita' ? '+' : '-'}{transaction.value}
                      </p>
                      <p className="text-xs text-gray-500">{transaction.time} atrás</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Curitiba Routes Map */}
      {selectedService === 'routes' && (
        <section class="pb-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-6">
                Mapa de Rotas - Curitiba
              </h3>
              
              {/* Layout em duas colunas */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Coluna da esquerda - Lista de Rotas */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-800 mb-4">Rotas Ativas</h4>
                  
                  {routesMetrics.restaurants.map((restaurant) => (
                    <div key={restaurant.id}>
                      <h5 className="font-semibold text-gray-800 mb-3 flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          restaurant.color === 'blue' ? 'bg-blue-500' : 
                          restaurant.color === 'green' ? 'bg-green-500' : 'bg-orange-500'
                        }`}></div>
                        {restaurant.name}
                      </h5>
                      {restaurant.routes.map((route, routeIndex) => (
                        <div key={routeIndex} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border mb-2">
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              restaurant.color === 'blue' ? 'bg-blue-100 text-blue-600' : 
                              restaurant.color === 'green' ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'
                            }`}>
                              <span className="text-xs font-bold">{route.id}</span>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{route.name}</p>
                              <p className="text-sm text-gray-600">{route.destination} • {route.deliveries} itens</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-900 font-semibold">{route.eta}</p>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              route.status === 'ativo' || route.status === 'Entregue' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {route.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Coluna da direita - Mapa */}
                <div className="space-y-4">
                  <div className="rounded-xl overflow-hidden shadow-lg">
                    <APIProvider apiKey="AIzaSyBbSslVR7hpIHFc6HMxrKpVT7NREZGZrG4">
                      <Map
                        style={mapContainerStyle}
                        defaultCenter={center}
                        defaultZoom={13}
                        mapId="CURITIBA_ROUTES_MAP"
                      >
                        {/* Marcadores dos Restaurantes */}
                        <AdvancedMarker
                          position={pontoSaida}
                          title={pontoSaida.name}
                        >
                          <RestaurantMarker color="#3B82F6" />
                        </AdvancedMarker>
                        
                        <AdvancedMarker
                          position={ponto1}
                          title={ponto1.name}
                        >
                          <Pin background="#10B981" borderColor="#FFFFFF" glyphColor="#FFFFFF" />
                        </AdvancedMarker>
                        
                        <AdvancedMarker
                          position={ponto2}
                          title={ponto2.name}
                        >
                          <Pin background="#F59E0B" borderColor="#FFFFFF" glyphColor="#FFFFFF" />
                        </AdvancedMarker>

                        <AdvancedMarker
                          position={ponto3}
                          title={ponto3.name}
                        >
                          <Pin background="#EF4444" borderColor="#FFFFFF" glyphColor="#FFFFFF" />
                        </AdvancedMarker>
                        {/* Rotas Reais usando Directions API */}
                        <RoutesManager />
                      </Map>
                    </APIProvider>
                  </div>

                  {/* Legenda */}
                  <div className="mt-4 flex flex-wrap gap-4 justify-center">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Restaurante Prado Velho (3 rotas)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Ponto 1</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Ponto 2</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Ponto 3</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm text-gray-600">3 rotas ativas</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Demo Notice */}
      <section className="py-12 bg-gradient-to-r from-slate-900 via-gray-900 to-zinc-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <FiEye className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Este é Apenas uma Demonstração
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Viu como nossos dashboards podem transformar a gestão do seu negócio? 
              Entre em contato conosco para implementar essas soluções na sua empresa.
            </p>
            <div className="flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/contact')}
                className="bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all duration-300 btn-hover"
              >
                Solicitar Demonstração Real
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard; 